/* Copyright 2013-2020 The MathWorks, Inc. */

define([
    'dojo/_base/declare',
    'dijit/_WidgetBase',
    'dijit/_TemplatedMixin',
    'dijit/_Container',
    'dijit/registry',
    'dojo/keys',
    'dojo/text!./templates/QAB.html',
    'dojo/i18n!l10n/nls/toolstrip',
    './_WidgetMixin',
    'dojo/aspect',
    'MW/toolstrip/constants/TypeConstants',
    'MW/toolstrip/menu/PopupList',
    'MW/toolstrip/form/DropDownButton',
    'MW/toolstrip/utils/DragSorter',
    'MW/uiframework/utils'
], function (declare, _WidgetBase, _TemplatedMixin, _Container, registry, keys, template, toolstripL10n, _WidgetMixin,
    aspect, TypeConstants, PopupList, DropDownButton, DragSorter, utils) {
    return declare([_WidgetBase, _TemplatedMixin, _Container, _WidgetMixin], {

        templateString: template,
        _isOverflowButtonVisible: false,
        _overflowButtonWidth: 0,

        constructor: function () {
            this.own(
                // Always add new children at 0 location in a Quick Access Bar, if no index is provided.
                aspect.before(this, 'addChild', function (widget, index) {
                    var newIndex = 0;
                    if (index >= 0) {
                        newIndex = index;
                    }
                    return [widget, newIndex];
                }),
                aspect.after(this, 'addChild', function () {
                    this.getChildren().forEach(function (child, index) {
                        if (!this._overflowPopup._qaGroups[child.id]) {
                            // Create placeholder div in the overflow popup corresponding to the QA group in the QAB
                            this._overflowPopup._qaGroups[child.id] = document.createElement('div');
                            this._overflowPopup._qaGroups[child.id].setAttribute('id', child.id + '_overflow');
                            this._overflowPopup._qaGroups[child.id].classList.add('overflowPopupQAGroup');

                            // Place the placeholder QA group div at the right place within the overflow popup
                            this._overflowPopup.domNode.insertBefore(this._overflowPopup._qaGroups[child.id], this._overflowPopup.domNode.childNodes[index]);

                            // Create dragSorterContainer for the overflow popup QA group
                            var dragSorterContainer = document.createElement('div');
                            dragSorterContainer.classList.add('dragSorterContainer');

                            // Append the dragSorterContainer to the placeholder QA group div created above
                            this._overflowPopup._qaGroups[child.id].appendChild(dragSorterContainer);

                            if (this.enableQABCustomization) { // Allow drag and drop to rearrange QAB widgets in the overflow popup only when QAB customization is on
                                this._overflowPopup._dragSorters[child.id] = new DragSorter({
                                    containerNode: dragSorterContainer,
                                    containerNodeWithinPopup: true,
                                    dragOrientation: 'vertical'
                                });

                                this._overflowPopup._dragSorters[child.id].on('nodemoved', function (data) {
                                    var qaGroup = registry.byId(child.id);
                                    var qaGroupChildren = qaGroup.getChildren();

                                    // Calculating the right offset value for the index is important here since there
                                    // might be some QAB widgets that are not yet collapsed to the overflow popup.
                                    var offset = 0;
                                    for (var i = 0; i < qaGroupChildren.length; i++) {
                                        if (qaGroupChildren[i]._isShed) {
                                            break;
                                        } else {
                                            offset = offset + 1;
                                        }
                                    }

                                    var toolstrip = this.getParent();

                                    // Set resize flag to avoid resizing the toolstrip since we do not want any resize updates
                                    toolstrip.setResizeFlag(false);

                                    var oldIndex = data.oldIndex + offset;
                                    var newIndex = data.newIndex + offset;
                                    var itemId = qaGroup.getChildren()[oldIndex].id;

                                    // Fire itemMoved event so that the model gets updated with the right index
                                    qaGroup.itemMoved({
                                        itemId: itemId,
                                        newIndex: newIndex
                                    });

                                    // Reset resize flag
                                    toolstrip.setResizeFlag(true);

                                    // Updating the model can move the QAB widget again within the original QAB, make
                                    // sure that the same widget is at the right updated place in the overflow popup.
                                    dragSorterContainer.insertBefore(registry.byId(itemId).domNode.parentElement, dragSorterContainer.childNodes[data.newIndex]);
                                }.bind(this));
                            }
                        }
                    }.bind(this));
                    this.refresh();
                }),
                aspect.after(this, 'removeChild', function () {
                    var qaGroupIds = [];
                    this.getChildren().forEach(function (child) {
                        qaGroupIds.push(child.id);
                    });

                    for (var qaGroupId in this._overflowPopup._qaGroups) {
                        if (this._overflowPopup._qaGroups.hasOwnProperty(qaGroupId) && qaGroupIds.indexOf(qaGroupId) === -1) {
                            if (this.enableQABCustomization) {
                                // Destroy DragSorter
                                this._overflowPopup._dragSorters[qaGroupId].destroy();
                                delete this._overflowPopup._dragSorters[qaGroupId];
                            }

                            // Remove the QA group div from the overflow popup
                            this._overflowPopup._qaGroups[qaGroupId].remove();
                            delete this._overflowPopup._qaGroups[qaGroupId];
                        }
                    }

                    this.refresh();
                })
            );
        },

        postCreate: function () {
            this.inherited(arguments);
            this.refresh();
            if (this.domNode) {
                // Make screen reader announce the quick access bar grouping when the focus enters within its widget
                // from outside of its DOM node.
                this.domNode.setAttribute('role', 'group');
                this.domNode.setAttribute('aria-label', toolstripL10n.QAB);
            }

            this._setupForOverflow();
        },

        startup: function () {
            this.inherited(arguments);
            this._overflowDropDown.startup();
            this._overflowPopup.startup();
        },

        refresh: function () {
            this.showHideQAB();
            this.showHideQAGroups();
            this.showHideSeparators();
        },

        showHideQAB: function () {
            if (this.qabDropDownContainer && this.qabDropDownContainer.hasChildNodes()) {
                this.domNode.style.display = 'block';
                return;
            }

            var numOfControls = 0;

            this.getChildren().forEach(function (qaGroup) {
                numOfControls += qaGroup.getChildren().length;
            });
            if (numOfControls === 0) {
                this.domNode.style.display = 'none';
            } else {
                this.domNode.style.display = 'block';
            }
        },

        showHideQAGroups: function () {
            this.getChildren().forEach(function (qaGroup) {
                if (qaGroup.getChildren().length === 0) {
                    qaGroup.domNode.style.display = 'none';
                } else {
                    qaGroup.domNode.style.display = 'inline-block';
                }
            }, this);
        },

        showHideSeparators: function () {
            var qaGroups = this.getChildren();
            qaGroups.forEach(function (qaGroup, index) {
                // Separator does not need to be shown for the first visible quick access group
                if (this._isFirstVisibleQAGroup(qaGroup, index)) {
                    qaGroup.separatorNode.style.display = 'none';
                } else {
                    qaGroup.separatorNode.style.display = 'inline-block';
                }
            }, this);
        },

        // This function returns true if the given qaGroup is the left most visible qa group.
        _isFirstVisibleQAGroup: function (qaGroup, index) {
            var qaGroups = this.getChildren(), i, numQAGroups = qaGroups.length, result = false;
            i = 0;
            // If you are the first child, do not show separator
            if (index === 0) {
                return true;
            }
            // Loop[ through all previous childs anc check if they are display none or not
            while (i <= index - 1) {
                if (qaGroups[i].domNode.style.display === 'none') {
                    result = true;
                }
                i += 1;
            }
            return result;
        },

        restoreFactoryControls: function () {},

        _setupForOverflow: function () {
            this._overflowPopup = new PopupList({
                type: TypeConstants.POPUP_LIST,
                tag: 'qabOverflowPopup'
            });

            this._overflowDropDown = new DropDownButton({
                type: TypeConstants.QAB_DROP_DOWN_BUTTON,
                tag: 'qabOverflowButton',
                text: toolstripL10n.qabOverflowDropdownLabel,
                disabled: true, // Disabled to ensure that keyboard navigation is not affected in the toolstrip when this button is hidden as disabled buttons do not have tabindex attributes.
                menu: this._overflowPopup,
                showText: false
            });

            this.qabOverflowContainer.appendChild(this._overflowDropDown.domNode);
            this._hideOverflowButton(); // Hide the overflow button by default

            this._overflowPopup.domNode.classList.add('qabOverflowPopup');
            this.own(this._overflowPopup.on('open', this._overflowPopupOnOpen.bind(this)));
            this._overflowPopup._qaGroups = {};
            this._overflowPopup._dragSorters = {};
        },

        _overflowPopupOnOpen: function () {
            if (!this._overflowPopupListener) {
                // Add a listener to handle the keyboard navigation within the overflow popup
                this._overflowPopupListener = utils.addEventListener(this._overflowPopup.domNode.parentElement, 'keydown', this._handleKeyDownInOverflowPopup.bind(this), true);
            }
        },

        /**
         * Handles keyboard navigation within the overflow popup. 'Tab' and 'Down Arrow' keys will move the focus forward
         * whereas 'Shift + Tab' and 'Up Arrow' keys will move the focus backward.
         * @param {Object} evt - event object
         */
        _handleKeyDownInOverflowPopup: function (evt) {
            var validKeyCodes = [keys.TAB, keys.UP_ARROW, keys.DOWN_ARROW];
            var keyCode = evt.keyCode;
            var targetIndex;
            if (validKeyCodes.indexOf(keyCode) > -1) {
                var focusableNodes = this._overflowPopup.domNode.querySelectorAll('[tabindex="0"]');
                var focusedElement = document.activeElement;
                var focusedElementIndex = Array.from(focusableNodes).indexOf(focusedElement);

                if (focusedElementIndex === -1) {
                    // Focus is on the overflow popup
                    if ((keyCode === keys.TAB && !evt.shiftKey) || keyCode === keys.DOWN_ARROW) {
                        targetIndex = 0;
                    } else if ((keyCode === keys.TAB && evt.shiftKey) || keyCode === keys.UP_ARROW) {
                        targetIndex = focusableNodes.length - 1;
                    }
                } else {
                    // Focus is inside overflow popup
                    if ((keyCode === keys.TAB && !evt.shiftKey) || keyCode === keys.DOWN_ARROW) {
                        if (focusedElementIndex === focusableNodes.length - 1) {
                            targetIndex = 0;
                        } else {
                            targetIndex = focusedElementIndex + 1;
                        }
                    } else if ((keyCode === keys.TAB && evt.shiftKey) || keyCode === keys.UP_ARROW) {
                        if (focusedElementIndex === 0) {
                            targetIndex = focusableNodes.length - 1;
                        } else {
                            targetIndex = focusedElementIndex - 1;
                        }
                    }
                }

                if (typeof targetIndex === 'number' && focusableNodes[targetIndex]) {
                    focusableNodes[targetIndex].focus();
                    evt.preventDefault();
                    evt.stopImmediatePropagation();
                }
            }
        },

        /**
         * Shows the overflow button
         */
        _showOverflowButton: function () {
            this._overflowDropDown.set('mnemonic', 'Z');
            this._overflowDropDown.set('disabled', false);
            this.containerNode.classList.remove('noOverflow');
            this.qabOverflowContainer.classList.remove('noOverflow');
            this._overflowDropDown.domNode.classList.remove('hideQabOverflowDropDownButton');
            this._isOverflowButtonVisible = true;
            if (this._overflowButtonWidth === 0) {
                this._overflowButtonWidth = parseFloat(window.getComputedStyle(this._overflowDropDown.domNode).width);
            }
        },

        /**
         * Hides the overflow button
         */
        _hideOverflowButton: function () {
            this._overflowDropDown.focusNode.setAttribute('data-mnemonic', '');
            this._overflowDropDown.set('disabled', true);
            this.containerNode.classList.add('noOverflow');
            this.qabOverflowContainer.classList.add('noOverflow');
            this._overflowDropDown.domNode.classList.add('hideQabOverflowDropDownButton');
            this._isOverflowButtonVisible = false;
        },

        /**
         * Adds the node to the overflow popup
         * @param {HTMLElement} node - Node to be added to the overflow popup
         * @param {Object} qaGroup - Its parent (QAGroup widget)
         * @param {Boolean} isSeparator - True if the node is a QAGroup separator
         */
        _addToOverflowPopup: function (node, qaGroup, isSeparator) {
            if (isSeparator) {
                this._overflowPopup._qaGroups[qaGroup.id].insertBefore(node, this._overflowPopup._qaGroups[qaGroup.id].childNodes[0]);
            } else {
                this._overflowPopup._qaGroups[qaGroup.id].lastElementChild.insertBefore(node, this._overflowPopup._qaGroups[qaGroup.id].lastElementChild.childNodes[0]);
            }
        },

        /**
         * Removes the node from the overflow popup and places it back in QAB
         * @param {HTMLElement} node -  Node to be removed from the overflow popup
         * @param {HTMLElement} qaGroup - Its parent node (QAGroup element in the QAB)
         * @param {Boolean} isSeparator - True if the node is a QAGroup separator
         */
        _removeFromOverflowPopup: function (node, qaGroup, isSeparator) {
            if (isSeparator) {
                qaGroup.insertBefore(node, qaGroup.childNodes[0]);
            } else {
                // Append this to qaGroup's containerNode to ensure that dragSorter logic works fine.
                qaGroup.lastElementChild.appendChild(node);
            }
        },

        /**
         * Calculates the width of the node.
         * @param {HTMLElement} node - node whose width is to be calculated
         */
        _calculateWidth: function (node) {
            var computedStyles = window.getComputedStyle(node);
            var contentWidth = parseFloat(computedStyles.width);
            var paddingLeft = parseFloat(computedStyles.paddingLeft);
            var paddingRight = parseFloat(computedStyles.paddingRight);
            var marginLeft = parseFloat(computedStyles.marginLeft);
            var marginRight = parseFloat(computedStyles.marginRight);

            return contentWidth + paddingLeft + paddingRight + marginLeft + marginRight;
        },

        /**
         * Handles the QAB collapse. It will start shedding one QAB widget at a time from right to left depending on the
         * overlapped width until there is no more overlap between QAB and toolstrip tabs.
         * @param {Number} overlappedWidth - overlapped width in pixels
         */
        collapse: function (overlappedWidth) {
            var qaGroups = this.getChildren();
            var qabWidgets;
            var collapsedWidth = 0;

            // Loop through QA groups from right to left
            for (var i = qaGroups.length - 1; i >= 0; i--) {
                    qabWidgets = qaGroups[i].getChildren();

                    // Loop through QAB widgets from right to left
                    for (var j = qabWidgets.length - 1; j >= 0; j--) {
                        if (i < qaGroups.length - 1 && !qaGroups[i + 1]._isShed && qaGroups[i + 1].getChildren().length > 0) {
                            // If the previous QA group had any QAB widgets and all of them are in the overflow popup, and
                            // this QA group also has some QAB widgets, then move the separator of previous QA group to
                            // the overflow popup.
                            qaGroups[i + 1].separatorNodeComputedWidth = this._calculateWidth(qaGroups[i + 1].separatorNode); // Cache the width so that we do not have to calculate it during QAB expansion
                            collapsedWidth += qaGroups[i + 1].separatorNodeComputedWidth;
                            this._addToOverflowPopup(qaGroups[i + 1].separatorNode, qaGroups[i + 1], true);
                            qaGroups[i + 1]._isShed = true;
                        }
                        if (!qabWidgets[j]._isShed) { // QAB widget is not yet shed
                            qabWidgets[j]._computedWidthWithWrapper = this._calculateWidth(qabWidgets[j].domNode.parentElement); // Cache the width so that we do not have to calculate it during QAB expansion
                            collapsedWidth += qabWidgets[j]._computedWidthWithWrapper;
                            qabWidgets[j]._isShed = true;
                            if (!qabWidgets[j].get('showText')) {
                                qabWidgets[j]._restrictModelUpdate = true; // Set the flag to make sure that the model is not updated for 'showText' property
                                qabWidgets[j].set('showText', true); // To always show the label in the overflow popup
                                qabWidgets[j].showText = false; // Setting the value to false again so that the widget returns the correct value of 'showText' property
                                delete qabWidgets[j]._restrictModelUpdate;
                            }

                            // Add the QAB widget to the overflow popup
                            this._addToOverflowPopup(qabWidgets[j].domNode.parentElement, qaGroups[i]);

                            if (this._isOverflowButtonVisible && collapsedWidth >= overlappedWidth) {
                                // There is no overlap now with the QAB widgets currently moved to the overflow popup. Stop here.
                                return;
                            } else if (!this._isOverflowButtonVisible) {
                                // First QAB widget getting collapsed, show the overflow button
                                this._showOverflowButton();
                                if (collapsedWidth >= overlappedWidth + this._overflowButtonWidth) { // Checking the overlap condition needs to take into account the overflow button width as well
                                    // No more overlap. Stop here.
                                    return;
                                }
                            }
                        }
                    }
                }
        },

        /**
         * Handles the QAB expand. It will start moving one QAB widget at a time (left to right direction) from the overflow
         * popup to the original QAB until there is enough width available such that there is no overlap between QAB and
         * toolstrip tabs.
         * @param {Number} availableWidth - available width in pixels
         */
        expand: function (availableWidth) {
            var qaGroups = this.getChildren();
            var qabWidgets;
            var expandedWidth = 0;

            // Loop through QA groups from left to right
            for (var i = 0; i < qaGroups.length; i++) {
                qabWidgets = qaGroups[i].getChildren();

                // Loop through QAB widgets from left to right
                for (var j = 0; j < qabWidgets.length; j++) {
                    if (qabWidgets[j]._isShed) {
                        expandedWidth += qabWidgets[j]._computedWidthWithWrapper; // Use the cached width of the widget before it was moved to the overflow popup
                        if (j === qabWidgets.length - 1 && i < qaGroups.length - 1 && qaGroups[i + 1]._isShed) {
                            expandedWidth += qaGroups[i + 1].separatorNodeComputedWidth; // Use the cached width of the separator before it was moved to the overflow popup
                        }
                        if (expandedWidth <= availableWidth) {
                            if (!qabWidgets[j].get('showText')) {
                                qabWidgets[j]._restrictModelUpdate = true; // Set the flag to make sure that the model is not updated for 'showText' property
                                qabWidgets[j].set('showText', false); // Hide the QAB widget label
                            }

                            delete qabWidgets[j]._computedWidthWithWrapper;
                            delete qabWidgets[j]._restrictModelUpdate;
                            qabWidgets[j]._isShed = false;

                            // Remove the QAB widget from the overflow popup and move it back to QAB
                            this._removeFromOverflowPopup(qabWidgets[j].domNode.parentElement, qaGroups[i].domNode, false);

                            if (j === qabWidgets.length - 1 && i < qaGroups.length - 1 && qaGroups[i + 1]._isShed) {
                                // This is the last QAB widget in this QA group. If the next QA group is also shed, then
                                // we need to start with moving its separatorNode back to QAB.
                                delete qaGroups[i + 1].separatorNodeComputedWidth;
                                qaGroups[i + 1]._isShed = false;

                                // Remove the separator node from the overflow popup and move it back to QAB
                                this._removeFromOverflowPopup(qaGroups[i + 1].separatorNode, qaGroups[i + 1].domNode, true);
                            }
                        } else {
                            // No more width available. Stop here.
                            return;
                        }
                    }
                }
            }

            if (expandedWidth <= availableWidth) {
                // There is still more width available and we have expanded all the QAB widgets. So, hide the overflow button.
                this._hideOverflowButton();
            }
        },

        destroy: function () {
            if (this._overflowPopupListener) {
                this._overflowPopupListener.remove();
            }

            this.inherited(arguments);
        }
    });
});
